import { DomainError } from "./errors";

/** Saldo de um produto num depósito (q4). Disponível = físico − reservado (05 §5). */
export type StockPosition = { physicalQ4: number; reservedQ4: number };

/**
 * Operações sobre a posição: `in` entrada (E), `out` saída (S), `count` balanço (B: o físico passa a ser a quantidade
 * contada), `reserve` reserva, `release` liberação de reserva, `consume` baixa da reserva (saída do que estava
 * reservado). Transferência = `out` na origem + `in` no destino.
 */
export type StockOp = {
  type: "in" | "out" | "count" | "reserve" | "release" | "consume";
  qtyQ4: number;
};

/** Maior quantidade de um movimento: 100 milhões de unidades (q4 × 10^12, longe de 2^53 nas somas). */
export const MAX_STOCK_QTY_Q4 = 1_000_000_000_000;

/**
 * Teto do saldo (físico em módulo e reservado) de um produto num depósito, e do físico somado dos depósitos: 100
 * bilhões de unidades (q4 × 10^15). Fica abaixo de 2^53 com folga para as somas e a conta do custo médio.
 */
export const MAX_STOCK_BALANCE_Q4 = 1_000_000_000_000_000;

const stockLimit = (): never => {
  throw new DomainError(
    "stock_limit",
    "O saldo passaria do limite de 100 bilhões de unidades. Confira a quantidade ou faça um balanço.",
  );
};

function assertInt(v: number, what: string) {
  if (!Number.isSafeInteger(v)) throw new DomainError("not_integer", `${what} deve ser inteiro: ${v}`);
}

function assertQty(qtyQ4: number, allowZero: boolean) {
  if (!Number.isSafeInteger(qtyQ4) || qtyQ4 < (allowZero ? 0 : 1) || qtyQ4 > MAX_STOCK_QTY_Q4)
    throw new DomainError("invalid_qty", `quantidade inválida: ${qtyQ4}`);
}

function assertCents(v: number, what: string) {
  assertInt(v, what);
  if (v < 0) throw new DomainError("invalid_money", `${what} não pode ser negativo: ${v}`);
}

export function availableQ4(p: StockPosition): number {
  return p.physicalQ4 - p.reservedQ4;
}

const insufficient = (): never => {
  throw new DomainError("insufficient_stock", "Estoque insuficiente.");
};

/** Valor acima do teto que se afasta de zero (o que se aproxima passa: conserta dado antigo acima do teto). */
const grows = (next: number, prev: number) =>
  Math.abs(next) > MAX_STOCK_BALANCE_Q4 && Math.abs(next) > Math.abs(prev);

/**
 * Aplica uma operação à posição e devolve a nova posição e a variação do físico (`deltaQ4`, o que vai para o extrato).
 * Com o negativo bloqueado (`settings.stock.allow_negative` desligado): saída e reserva exigem disponível suficiente
 * (o reservado é de outro pedido); a baixa da reserva exige físico final ≥ 0; o balanço não fica abaixo do reservado.
 * Entrada nunca bloqueia. A posição resultante respeita MAX_STOCK_BALANCE_Q4 (`stock_limit`).
 */
export function applyStockOp(
  pos: StockPosition,
  op: StockOp,
  opts: { allowNegative: boolean },
): { position: StockPosition; deltaQ4: number } {
  const r = applyOp(pos, op, opts);
  if (grows(r.position.physicalQ4, pos.physicalQ4) || grows(r.position.reservedQ4, pos.reservedQ4))
    stockLimit();
  return r;
}

function applyOp(
  pos: StockPosition,
  op: StockOp,
  opts: { allowNegative: boolean },
): { position: StockPosition; deltaQ4: number } {
  // O balanço substitui o físico: o anterior só precisa ser inteiro (pode ter passado de 2^53 antes do teto).
  if (op.type === "count") {
    if (!Number.isInteger(pos.physicalQ4)) assertInt(pos.physicalQ4, "físico");
  } else assertInt(pos.physicalQ4, "físico");
  assertInt(pos.reservedQ4, "reservado");
  assertQty(op.qtyQ4, op.type === "count");
  const q = op.qtyQ4;
  const { physicalQ4: phys, reservedQ4: res } = pos;
  const block = !opts.allowNegative;
  switch (op.type) {
    case "in":
      return { position: { physicalQ4: phys + q, reservedQ4: res }, deltaQ4: q };
    case "out":
      if (block && phys - res - q < 0) insufficient();
      return { position: { physicalQ4: phys - q, reservedQ4: res }, deltaQ4: -q };
    case "count":
      if (block && q < res)
        throw new DomainError(
          "count_below_reserved",
          "A contagem fica abaixo do que está reservado para pedidos. Libere ou baixe as reservas antes do balanço, " +
            "ou permita estoque negativo nas configurações.",
        );
      return { position: { physicalQ4: q, reservedQ4: res }, deltaQ4: q - phys };
    case "reserve":
      if (block && phys - res - q < 0) insufficient();
      return { position: { physicalQ4: phys, reservedQ4: res + q }, deltaQ4: 0 };
    case "release":
      if (q > res) throw new DomainError("invalid_reservation", "Liberação maior que o reservado.");
      return { position: { physicalQ4: phys, reservedQ4: res - q }, deltaQ4: 0 };
    case "consume":
      if (q > res) throw new DomainError("invalid_reservation", "Baixa maior que o reservado.");
      if (block && phys - q < 0) insufficient();
      return { position: { physicalQ4: phys - q, reservedQ4: res - q }, deltaQ4: -q };
  }
}

/** Divisão inteira com arredondamento comercial (meio para longe do zero). `den` > 0. */
function roundHalfUpBig(num: bigint, den: bigint): bigint {
  if (num < 0n) return -roundHalfUpBig(-num, den);
  return (2n * num + den) / (2n * den);
}

const roundHalfUp = (num: bigint, den: bigint): number => Number(roundHalfUpBig(num, den));

type CostInput = { physicalQ4: number; avgCostCents: number; qtyQ4: number; unitCostCents: number };

function assertCostInput(i: CostInput) {
  assertInt(i.physicalQ4, "físico");
  assertCents(i.avgCostCents, "custo médio");
  assertCents(i.unitCostCents, "custo unitário");
  assertQty(i.qtyQ4, false);
}

/**
 * Custo médio depois de uma entrada (05 §5): `(físico × média + qtd × custo) / (físico + qtd)`; com físico ≤ 0 a média
 * passa a ser o custo da entrada. `físico` é o saldo total do produto antes da entrada. Conta em BigInt, meio para cima.
 */
export function nextAverageCost(i: CostInput): number {
  assertCostInput(i);
  if (i.physicalQ4 <= 0) return i.unitCostCents;
  const num = BigInt(i.physicalQ4) * BigInt(i.avgCostCents) + BigInt(i.qtyQ4) * BigInt(i.unitCostCents);
  return roundHalfUp(num, BigInt(i.physicalQ4) + BigInt(i.qtyQ4));
}

/**
 * Custo médio depois do estorno de uma entrada (qtd ao custo da entrada sai do saldo `físico`): desfaz a ponderação,
 * `(físico × média − qtd × custo) / (físico − qtd)`. Sem saldo restante, ou se a conta der custo negativo (a média já
 * mudou por outras entradas), a média fica como está.
 */
export function averageCostAfterReversal(i: CostInput): number {
  assertCostInput(i);
  const rest = BigInt(i.physicalQ4) - BigInt(i.qtyQ4);
  if (rest <= 0n) return i.avgCostCents;
  const num = BigInt(i.physicalQ4) * BigInt(i.avgCostCents) - BigInt(i.qtyQ4) * BigInt(i.unitCostCents);
  if (num < 0n) return i.avgCostCents;
  return roundHalfUp(num, rest);
}

/**
 * Valor do estoque: Σ físico × custo médio (centavos, arredondamento comercial item a item). Conta em BigInt e devolve
 * null quando o valor passa de 2^53 (ou a entrada não é inteira): a leitura do saldo nunca falha por causa do valor.
 */
export function stockValueCents(
  items: readonly { physicalQ4: number; avgCostCents: number }[],
): number | null {
  let total = 0n;
  for (const i of items) {
    if (!Number.isSafeInteger(i.physicalQ4) || !Number.isSafeInteger(i.avgCostCents)) return null;
    total += roundHalfUpBig(BigInt(i.physicalQ4) * BigInt(i.avgCostCents), 10_000n);
  }
  const n = Number(total);
  return Number.isSafeInteger(n) ? n : null;
}

/**
 * Físico somado dos depósitos (base do custo médio). Recebe os saldos como texto do SQLite (exatos mesmo acima de 2^53)
 * ou número e soma em BigInt; passou de MAX_STOCK_BALANCE_Q4, `stock_limit`.
 */
export function sumPhysicalQ4(values: readonly (string | number)[]): number {
  let total = 0n;
  for (const v of values) total += BigInt(v);
  const limit = BigInt(MAX_STOCK_BALANCE_Q4);
  if (total > limit || total < -limit) stockLimit();
  return Number(total);
}

/** Movimento que desfaz outro: quem somou ao físico é estornado por uma saída, quem tirou, por uma entrada. */
export function reversalOf(deltaQ4: number): { kind: "E" | "S"; qtyQ4: number } {
  assertInt(deltaQ4, "diferença");
  if (deltaQ4 === 0) throw new DomainError("nothing_to_reverse", "Movimento sem diferença de saldo.");
  return deltaQ4 > 0 ? { kind: "S", qtyQ4: deltaQ4 } : { kind: "E", qtyQ4: -deltaQ4 };
}
